// Советы и заявки агента в «Решениях» (PLAN §2 «Режимы автономии»). Советы «в тень» владелец отмечает
// «согласен / не согласен»; заявки (правила методики, лендинги, генерации, батчи, действия в рекламе)
// одобряет или отклоняет — выполняет платформа, заново сверив лимиты.
// Карточка говорит простыми словами: что агент хочет, зачем, что сделает платформа, чем это подтверждено
// (у заявок по рекламе — мини-график расхода, лидов и CPL объекта), почему решать вам. Тип действия, id,
// поля и сам запрос в Meta — под «Показать технически».
import { Button, Chip, inTime, int, Sparkline, signedUsd, toast, usd, when } from '@autoaff/ui';
import { Link } from '@tanstack/react-router';
import { BookCheck, MessageCircle, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import type { Proposal } from '../agent-api';
import { api } from '../api';
import { chatStore } from '../chat/store';
import type { Series } from '../shell/waiting';
import { LaunchPlan, launchLine } from './LaunchPlan';

const DOMAIN: Record<string, string> = {
  ads: 'Реклама',
  budgets: 'Бюджеты',
  campaigns: 'Кампании',
  creatives: 'Креативы',
  offers: 'Офферы',
  landings: 'Лендинги',
  memory: 'Методика',
  other: 'Совет',
};

/** «Показать технически»: пути, команды, параметры — для тех, кто хочет проверить сам. */
export function TechDetails({ rows, children }: { rows: Array<[string, string]>; children?: ReactNode }) {
  if (!rows.length && !children) return null;
  return (
    <details className="tech">
      <summary>Показать технически</summary>
      {rows.length > 0 && (
        <dl className="tech-list">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {children}
    </details>
  );
}

/** Батч роликов от агента: оффер, сколько и пожелания. */
function batchLine(payload: Proposal['payload']): string {
  const o = (payload ?? {}) as { offer?: string; count?: number; brief?: string };
  return `${o.offer ?? '—'} · ${o.count ?? '?'} шт.${o.brief ? ` · ${o.brief}` : ''}`;
}

/** Тест оффера и смена статуса — словами; имя папки и поток — в «технически». */
function offerLine(p: Proposal): string {
  const o = (p.payload ?? {}) as { ext_id?: string | number; offer?: string; status?: string };
  return p.type === 'offer.test'
    ? `Взять оффер сети № ${o.ext_id ?? '—'} в тест`
    : `${o.offer ?? 'Оффер'}: новая стадия — ${o.status ?? '—'}`;
}

/** Что именно откроется: адрес и версии (с долями, если A/B). */
function publishLine(payload: Proposal['payload']): string {
  const o = (payload ?? {}) as {
    host?: string;
    path?: string;
    version_id?: number;
    variants?: Array<{ version_id?: number; weight?: number }>;
  };
  const path = o.path && o.path !== '/' ? `/${o.path.replace(/^\/+/, '')}` : '';
  const total = (o.variants ?? []).reduce((s, v) => s + (v.weight ?? 1), 0) || 1;
  const vs = o.variants?.length
    ? o.variants.map(
        (v) =>
          `версия ${v.version_id}${o.variants && o.variants.length > 1 ? ` — ${Math.round(((v.weight ?? 1) / total) * 100)}%` : ''}`,
      )
    : [`версия ${o.version_id}`];
  return `Открыть на ${o.host ?? '—'}${path}: ${vs.join(', ')}`;
}

const isMeta = (type: string) => type.startsWith('meta.');

interface MetaApiSummary {
  method?: string;
  path?: string;
  effect?: string;
  fields?: string[];
  spend?: number | 'unknown';
}
interface Policy {
  delta_usd?: number | null;
  allocated_usd?: number;
  web_taint?: string;
  reasons?: Array<{ rule?: string; text?: string }>;
  meta_api?: MetaApiSummary;
}
const policyOf = (p: Proposal) => (p.policy ?? {}) as Policy;

/** Что именно сделает платформа в Meta — по данным заявки, а не по словам агента. */
function metaLine(p: Proposal): string | null {
  // Любой запрос Graph API: что делает, посчитала платформа по пути и параметрам
  if (p.type === 'meta.api') return policyOf(p).meta_api?.effect ?? 'Запрос в рекламный кабинет Meta';
  if (p.type === 'meta.launch') return launchLine(p);
  const o = (p.payload ?? {}) as {
    id?: string;
    daily_usd?: number;
    activate?: boolean;
    adset?: { name?: string; daily_usd?: number };
    ads?: unknown[];
  };
  const who = p.target?.name ? `«${p.target.name}»` : '';
  if (p.type === 'meta.pause') return `Поставить на паузу ${who || 'объект рекламы'}`;
  if (p.type === 'meta.resume') return `Включить ${who || 'объект рекламы'}`;
  if (p.type === 'meta.budget' && o.daily_usd !== undefined)
    return who ? `Дневной бюджет ${who}: ${usd(o.daily_usd)}` : `Новый дневной бюджет: ${usd(o.daily_usd)}`;
  if (p.type === 'meta.duplicate')
    return `Копия адсета ${who}${o.daily_usd !== undefined ? ` с бюджетом ${usd(o.daily_usd)}` : ''}${o.activate ? ', сразу включить' : ', на паузе'}`;
  if (p.type === 'meta.create')
    return `Новый адсет «${o.adset?.name ?? ''}» ${o.adset?.daily_usd !== undefined ? usd(o.adset.daily_usd) : ''} в день, объявлений: ${o.ads?.length ?? 0}${o.activate ? ', сразу включить' : ', на паузе'}`;
  return null;
}

/** Что сделает платформа после одобрения — одной строкой словами. */
function effectLine(p: Proposal): ReactNode {
  if (p.type === 'rule') return String((p.payload as { text?: string } | null)?.text ?? '') || null;
  if (p.type === 'landing.publish') return publishLine(p.payload);
  if (p.type === 'creative.batch') return batchLine(p.payload);
  if (p.type === 'offer.test' || p.type === 'offer.status') return offerLine(p);
  if (p.type === 'channel.post')
    return (
      <>
        Пост в канал — текст и время <Link to="/agent/channel">в плане канала</Link>
      </>
    );
  return isMeta(p.type) ? metaLine(p) : null;
}

/** Как заявка меняет сумму дневных бюджетов — посчитано платформой; где посчитать нельзя — так и сказано. */
function spendLine(p: Proposal): string | null {
  const pol = policyOf(p);
  if (p.type === 'meta.api' && (pol.meta_api?.spend === 'unknown' || pol.delta_usd === null))
    return 'Влияние на расход не посчитать — решите вы';
  if (typeof pol.delta_usd !== 'number' || pol.delta_usd === 0) return null;
  return `${signedUsd(pol.delta_usd)} в день к сумме бюджетов${typeof pol.allocated_usd === 'number' ? ` (сейчас ${usd(pol.allocated_usd)})` : ''}`;
}

/**
 * Почему решать владельцу при любом режиме: метка интернета, назначения, неизвестный объект. «Расход не посчитать»
 * уже сказан строкой «Бюджеты» — здесь не повторяется (с именами полей — в «технически»).
 */
function reasonsOf(p: Proposal, spendShown: boolean): string[] {
  return (policyOf(p).reasons ?? []).flatMap((r) =>
    r.text && !(spendShown && r.rule === 'spend_unknown') ? [r.text] : [],
  );
}

/** Технические подробности заявки: тип действия, объект, поля, проверки платформы и сам запрос. */
function techRows(p: Proposal): Array<[string, string]> {
  const pol = policyOf(p);
  const rows: Array<[string, string]> = [['Действие', p.type]];
  if (p.target?.id) rows.push(['Объект', `${p.target.kind} ${p.target.id}`]);
  if (pol.meta_api?.fields?.length) rows.push(['Поля', pol.meta_api.fields.join(', ')]);
  const checks = (pol.reasons ?? []).flatMap((r) => (r.text ? [`${r.rule ?? '—'}: ${r.text}`] : []));
  if (checks.length) rows.push(['Проверки платформы', checks.join('\n')]);
  if (p.type !== 'meta.api' && p.payload) rows.push(['Данные заявки', JSON.stringify(p.payload, null, 2)]);
  return rows;
}

/** Сам запрос в Meta — для тех, кто хочет проверить параметры. */
function MetaRequest({ p }: { p: Proposal }) {
  const o = (p.payload ?? {}) as {
    method?: string;
    path?: string;
    params?: Record<string, unknown>;
    files?: Record<string, string>;
  };
  const head = `${o.method ?? 'POST'} /${String(o.path ?? '').replace(/^\/+/, '')}`;
  const body = JSON.stringify({ ...(o.params ?? {}), ...(o.files ? { files: o.files } : {}) }, null, 2);
  return (
    <div className="tech-req">
      <span>Запрос в Meta</span>
      <pre>{`${head}\n${body}`}</pre>
    </div>
  );
}

/** Направление цены лида: заметно дороже — красным, заметно дешевле — зелёным, иначе без цвета. */
function cplTone(values: number[]): 'danger' | 'success' | 'muted' {
  if (values.length < 4) return 'muted';
  const half = Math.floor(values.length / 2);
  const avg = (xs: number[]) => xs.reduce((t, x) => t + x, 0) / xs.length;
  const a = avg(values.slice(0, half));
  const b = avg(values.slice(-half));
  if (!a) return 'muted';
  return b / a >= 1.2 ? 'danger' : b / a <= 0.83 ? 'success' : 'muted';
}

/** Доказательство заявки по рекламе: расход, лиды и CPL объекта за 7 дней — цифры платформы, не слова агента. */
export function Evidence({ s }: { s: Series }) {
  const cpl = s.days.flatMap((d) => (d.leads ? [d.spend / d.leads] : []));
  return (
    <section className="ev" aria-label="Цифры объекта за 7 дней">
      <span className="ev-h">7 дней по дням, сегодня — неполный день</span>
      <div className="ev-cells">
        <div className="ev-cell">
          <span>Расход</span>
          <b className="tnum">{usd(s.spend)}</b>
          <Sparkline values={s.days.map((d) => d.spend)} tone="accent" />
        </div>
        <div className="ev-cell">
          <span>Лиды</span>
          <b className="tnum">{int(s.leads)}</b>
          <Sparkline values={s.days.map((d) => d.leads)} tone="accent" />
        </div>
        <div className="ev-cell">
          <span>CPL</span>
          <b className="tnum">{s.cpl === null ? '—' : usd(s.cpl)}</b>
          <Sparkline values={cpl} tone={cplTone(cpl)} fromZero={false} />
        </div>
      </div>
    </section>
  );
}

/** Главная кнопка заявки называет действие. */
function approveLabel(p: Proposal): string {
  if (p.type === 'rule') return 'Утвердить правило';
  if (p.type === 'gen.approve') return 'Разрешить генерацию';
  if (p.type === 'creative.batch') return 'Запустить батч';
  if (p.type === 'offer.test') return 'Начать тест';
  if (p.type === 'channel.post') return 'Одобрить пост';
  if (p.type === 'meta.launch') return 'Одобрить запуск';
  return 'Одобрить';
}

/**
 * Решение по заявке или совету: одно главное действие, отказ рядом; комментарий к отказу — по желанию (агент его
 * увидит: он хранится в заявке). compact — строка на «Сводке»: кнопки поменьше, без заливки.
 */
export function ProposalActions({
  p,
  onDone,
  compact,
}: {
  p: Proposal;
  onDone: () => void;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [asking, setAsking] = useState<'disagree' | 'reject' | null>(null);
  const rule = p.type === 'rule';
  // Ждёт решения владельца и после одобрения выполняется платформой (правило, публикация лендинга)
  const decide = p.status === 'pending';
  const size = compact ? ('sm' as const) : undefined;
  const send = async (path: string, body: Record<string, unknown>, done: string, key: string) => {
    setBusy(key);
    try {
      await api(`/api/agent/proposals/${p.id}/${path}`, body);
      toast(done);
      onDone();
    } catch (e) {
      toast((e as Error).message);
      setBusy(null);
    }
  };
  const approved = rule
    ? 'Правило утверждено — агент будет его соблюдать'
    : isMeta(p.type)
      ? 'Одобрено — платформа выполнит в Meta и напишет итог'
      : 'Одобрено — платформа выполнила';
  return (
    <div className={compact ? 'pa pa-compact' : 'pa'}>
      {asking && (
        <textarea
          className="input"
          rows={2}
          placeholder="Почему нет — агент учтёт (необязательно)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          aria-label="Комментарий для агента"
          // biome-ignore lint/a11y/noAutofocus: поле появилось по нажатию «Отклонить» — сразу печатать причину
          autoFocus
        />
      )}
      <div className="pa-row">
        {asking ? (
          <>
            <Button
              variant="danger-soft"
              size={size}
              disabled={!!busy}
              onClick={() =>
                asking === 'reject'
                  ? void send('decide', { decision: 'reject', comment }, 'Отклонено — агент учтёт', 'reject')
                  : void send(
                      'verdict',
                      { verdict: 'disagree', comment },
                      'Отмечено: не согласен',
                      'disagree',
                    )
              }
            >
              {asking === 'reject' ? (rule ? 'Отклонить правило' : 'Отклонить') : 'Не согласен'}
            </Button>
            <Button variant="ghost" size={size} onClick={() => setAsking(null)}>
              Отмена
            </Button>
          </>
        ) : decide ? (
          <>
            <Button
              variant={compact ? 'default' : 'primary'}
              size={size}
              icon={busy === 'approve' ? <span className="spinner" /> : <BookCheck />}
              disabled={!!busy}
              onClick={() => void send('decide', { decision: 'approve' }, approved, 'approve')}
            >
              {approveLabel(p)}
            </Button>
            <Button size={size} icon={<X />} disabled={!!busy} onClick={() => setAsking('reject')}>
              Отклонить
            </Button>
          </>
        ) : (
          <>
            <Button
              variant={compact ? 'default' : 'primary'}
              size={size}
              icon={busy === 'agree' ? <span className="spinner" /> : <ThumbsUp />}
              disabled={!!busy}
              onClick={() => void send('verdict', { verdict: 'agree' }, 'Отмечено: согласен', 'agree')}
            >
              Согласен
            </Button>
            <Button size={size} icon={<ThumbsDown />} disabled={!!busy} onClick={() => setAsking('disagree')}>
              Не согласен
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function ProposalCard({
  p,
  onDone,
}: {
  p: Proposal & { series?: Series | null };
  onDone: () => void;
}) {
  const rule = p.type === 'rule';
  const decide = p.status === 'pending';
  const spend = spendLine(p);
  const reasons = reasonsOf(p, Boolean(spend));
  const effect = effectLine(p);
  return (
    <article className="card prop">
      <div className="flex items-center gap-2 flex-wrap">
        <Chip tone={rule ? 'accent' : 'default'}>
          {rule ? 'Правило методики' : decide ? (DOMAIN[p.domain] ?? 'Заявка') : 'Совет агента'}
        </Chip>
        {policyOf(p).web_taint && <Chip tone="warning">идея из интернета</Chip>}
        <span className="muted wq-when">
          {when(p.createdAt)}
          {decide && p.expiresAt ? ` · ответ нужен ${inTime(p.expiresAt)}` : ''}
        </span>
      </div>
      <h3 className="prop-title">{p.title}</h3>
      {effect && (
        <blockquote className="prop-rule">
          {!rule && (
            <span className="prop-what">
              {decide ? 'Что сделает платформа после одобрения' : 'Что советует агент'}
            </span>
          )}
          {effect}
        </blockquote>
      )}
      {p.series && <Evidence s={p.series} />}
      <dl className="facts">
        <div>
          <dt>Зачем</dt>
          <dd>{p.reason}</dd>
        </div>
        {p.expectedEffect && (
          <div>
            <dt>Чего ждёт</dt>
            <dd>{p.expectedEffect}</dd>
          </div>
        )}
        {p.evidence?.text && (
          <div>
            <dt>Данные</dt>
            <dd>{p.evidence.text}</dd>
          </div>
        )}
        {p.type === 'meta.launch' && <LaunchPlan p={p} />}
        {spend && (
          <div>
            <dt>Бюджеты</dt>
            <dd className="tnum">{spend}</dd>
          </div>
        )}
        {reasons.length > 0 && (
          <div>
            <dt>Решать вам</dt>
            <dd>
              <ul className="prop-reasons">
                {reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </dd>
          </div>
        )}
      </dl>
      <TechDetails rows={techRows(p)}>{p.type === 'meta.api' && <MetaRequest p={p} />}</TechDetails>
      <div className="flex gap-2 flex-wrap items-start">
        <ProposalActions p={p} onDone={onDone} />
        {p.threadId && (
          <Button
            variant="ghost"
            icon={<MessageCircle />}
            onClick={() => chatStore.open({ threadId: p.threadId })}
            style={{ marginLeft: 'auto' }}
          >
            Обсудить с агентом
          </Button>
        )}
      </div>
    </article>
  );
}
